import { jsPDF } from "jspdf";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { saveAs } from "file-saver";

const MAX_EXPORT_MESSAGES = 500;

const toUtcString = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toISOString().replace("T", " ").replace("Z", " UTC");
};

const formatShortDateTime = (date) =>
  date.toLocaleString([], {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

const formatRelativeForToday = (diffMs) => {
  const minute = 60 * 1000;
  const hour = 60 * minute;
  if (diffMs < minute) return "just now";
  if (diffMs < hour) {
    const mins = Math.floor(diffMs / minute);
    return `${mins} min ago`;
  }
  const hrs = Math.floor(diffMs / hour);
  return `${hrs} hr ago`;
};

const formatMessages = (messages = []) =>
  messages.slice(-MAX_EXPORT_MESSAGES).map((msg) => {
    const sender =
      msg?.senderId?.EmpFirstName && msg?.senderId?.EmpLastName
        ? `${msg.senderId.EmpFirstName} ${msg.senderId.EmpLastName}`
        : msg?.senderName || msg?.senderId?.name || "Unknown";
    const text = msg?.message || "";
    const time = toUtcString(msg?.createdAt);
    const attachmentNames = (msg?.attachments || []).map((file) => file?.name).filter(Boolean);
    const attachmentLine = attachmentNames.length ? ` | Files: ${attachmentNames.join(", ")}` : "";
    return `[${time}] ${sender}: ${text}${attachmentLine}`;
  });

export const downloadChatAsPdf = (messages = [], chatName = "chat-history") => {
  const skipped = Math.max(messages.length - MAX_EXPORT_MESSAGES, 0);
  if (skipped > 0) {
    window.alert(`Large export detected. Exporting last ${MAX_EXPORT_MESSAGES} messages.`);
  }
  const doc = new jsPDF();
  const lines = formatMessages(messages);
  doc.setFontSize(12);
  doc.text(`Chat History: ${chatName}`, 10, 12);
  let y = 20;
  lines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line, 185);
    wrapped.forEach((part) => {
      if (y > 285) {
        doc.addPage();
        y = 12;
      }
      doc.text(part, 10, y);
      y += 7;
    });
  });
  doc.save(`${chatName}-history.pdf`);
};

export const downloadChatAsWord = async (messages = [], chatName = "chat-history") => {
  const skipped = Math.max(messages.length - MAX_EXPORT_MESSAGES, 0);
  if (skipped > 0) {
    window.alert(`Large export detected. Exporting last ${MAX_EXPORT_MESSAGES} messages.`);
  }
  const lines = formatMessages(messages);
  const paragraphs = [
    new Paragraph({
      children: [new TextRun({ text: `Chat History: ${chatName}`, bold: true })],
    }),
    ...lines.map((line) => new Paragraph(line)),
  ];

  const doc = new Document({
    sections: [{ properties: {}, children: paragraphs }],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${chatName}-history.docx`);
};

export const toUniversalUtc = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) {
    return formatRelativeForToday(now.getTime() - date.getTime());
  }
  return formatShortDateTime(date);
};
